import { useState, useRef, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useSnapshot } from 'valtio';
import { useRealtimePCMRecorder } from './useRealtimePCMRecorder';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';
import { RecordingDurationStore } from '@/utils/store/recordingDuration';
import { startMeetingTimer, stopMeetingTimer, resetMeetingTimer } from '@/utils/store/meetingDuration';
import { SegmentDurationStore, startSegmentTimer, stopSegmentTimer, resetSegmentTimer } from '@/utils/store/segmentDuration';
import { reportRecordingDuration } from '@/services/recordingDurationService';
import { handleError } from '@/utils/helpers';
import { createAudioFile } from '@/utils/audio-file-saver';

// 使用相对路径，通过 vite 代理转发到后端
const API_BASE_URL = '';

/**
 * Hook 配置选项
 */
export interface UseRealtimeTranscriptionOptions {
  /** 录音开始时的回调 */
  onStart?: () => void;
  /** 录音停止时的回调，返回完整转录文本 */
  onStop?: (transcription: string) => void;
  /** 转录更新时的回调（每次有新的 final 文本） */
  onTranscriptionUpdate?: (text: string, fullText: string) => void;
  /** 错误发生时的回调 */
  onError?: (error: string) => void;
  /** 部分转录更新时的回调 */
  onPartialUpdate?: (text: string) => void;
  /** 模块名称，用于错误日志 */
  moduleName?: string;
}

/**
 * Hook 返回值
 */
export interface UseRealtimeTranscriptionReturn {
  /** 是否正在录音 */
  isRecording: boolean;
  /** 是否已连接 SSE */
  isConnected: boolean;
  /** 最终转录文本 */
  finalText: string;
  /** 部分转录文本（实时） */
  partialText: string;
  /** 错误信息 */
  error: string | null;
  /** 麦克风权限状态 */
  microphonePermission: 'granted' | 'denied' | 'pending';
  /** 权限错误信息 */
  permissionError: string | null;
  /** 录音时长状态 */
  durationState: ReturnType<typeof useSnapshot<typeof RecordingDurationStore>>;
  /** 开始录音 */
  startRecording: () => Promise<void>;
  /** 停止录音 */
  stopRecording: () => Promise<void>;
  /** 清空转录内容 */
  clearTranscription: () => void;
  /** 申请麦克风权限 */
  requestMicrophonePermission: () => Promise<void>;
  /** 设置最终文本（外部控制） */
  setFinalText: (text: string | ((prev: string) => string)) => void;
  /** 获取录音文件（WAV 格式） */
  getRecordingFile: (fileName?: string) => File | null;
}

/**
 * 实时转录 Hook
 * 抽离了 SSE 连接、PCM 录音、麦克风权限、录音时长等 UI 无关逻辑
 * 可用于 OnlineMeet、InMeet 等页面
 */
export function useRealtimeTranscription(
  options: UseRealtimeTranscriptionOptions = {}
): UseRealtimeTranscriptionReturn {
  const {
    onStart,
    onStop,
    onTranscriptionUpdate,
    onError,
    onPartialUpdate,
    moduleName = 'RealtimeTranscription',
  } = options;

  const { user } = useSupabaseAuth();
  const durationState = useSnapshot(RecordingDurationStore);

  // 状态
  const [isRecording, setIsRecording] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [finalText, setFinalText] = useState('');
  const [partialText, setPartialText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [microphonePermission, setMicrophonePermission] = useState<
    'granted' | 'denied' | 'pending'
  >('pending');

  // Refs
  const eventSourceRef = useRef<EventSource | null>(null);
  const sessionIdRef = useRef<string>('');
  const finalTextRef = useRef<string>('');
  const retryCountRef = useRef<number>(0);
  const retryTimerRef = useRef<NodeJS.Timeout | null>(null);

  const maxRetries = 999; // 最大重试次数
  const retryDelay = 2000; // 重试延迟（毫秒）

  // 保持 finalText ref 同步
  useEffect(() => {
    finalTextRef.current = finalText;
  }, [finalText]);

  /**
   * 生成会话 ID
   */
  const generateSessionId = useCallback(() => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  /**
   * 设置错误并发送到远端日志系统
   */
  const setErrorWithLog = useCallback(
    (errorMessage: string, keyMsg: string = 'runtime-error', event?: any) => {
      setError(errorMessage);
      onError?.(errorMessage);

      const errorDetail = {
        message: errorMessage,
        timestamp: new Date().toISOString(),
        sessionId: sessionIdRef.current || 'no-session',
        isRecording,
        microphonePermission,
        event: event ? {
          type: event.type,
          target: event.target ? {
            readyState: event.target.readyState,
            url: event.target.url,
          } : undefined,
        } : undefined,
      };

      handleError(new Error(JSON.stringify(errorDetail)), errorMessage, moduleName);
    },
    [isRecording, microphonePermission, moduleName, onError]
  );

  /**
   * 发送 PCM 音频数据到后端
   */
  const sendPCMData = useCallback(
    async (pcmData: ArrayBuffer) => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/speech/audio?sessionId=${sessionIdRef.current}`,
          {
            method: 'POST',
            body: new Uint8Array(pcmData),
            headers: { 'Content-Type': 'application/octet-stream' },
          }
        );

        if (!response.ok) {
          const errorMsg = `发送音频数据失败: ${response.status} ${response.statusText}`;
          console.error(errorMsg);
          handleError(new Error(errorMsg), '发送音频数据失败', moduleName);
        }
      } catch (err) {
        console.error('发送音频数据错误:', err);
        handleError(err, '发送音频数据错误', moduleName);
      }
    },
    [moduleName]
  );

  /**
   * 录音数据处理回调
   */
  const onData = useCallback(
    (pcmData: ArrayBuffer) => {
      if (pcmData && pcmData.byteLength > 0) {
        sendPCMData(pcmData);
      }
    },
    [sendPCMData]
  );

  /**
   * 录音停止时的回调
   */
  const onRecordingStop = useCallback(
    (pcmData: ArrayBuffer) => {
      console.log('录音停止，最终音频数据:', pcmData.byteLength, 'bytes');
      if (pcmData && pcmData.byteLength > 0) {
        sendPCMData(pcmData);
      }
    },
    [sendPCMData]
  );

  // 使用自定义 PCM 录音 hook
  const {
    status: recorderStatus,
    startRecording: startRec,
    stopRecording: stopRec,
    getAllPcmData, // 获取所有录音数据
  } = useRealtimePCMRecorder({
    onData,
    onStop: onRecordingStop,
    sampleRate: 16000,
    channelCount: 1,
    dataInterval: 1000, // 每秒发送一次数据
  });

  /**
   * 建立 SSE 连接
   */
  const connectSSE = useCallback(async () => {
    // 重置重试计数器
    retryCountRef.current = 0;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const sessionId = generateSessionId();
    sessionIdRef.current = sessionId;

    const eventSource = new EventSource(
      `${API_BASE_URL}/api/speech/start?sessionId=${sessionId}`
    );
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('partial', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      console.log('部分转录结果:', data);
      setPartialText(data.text || '');
      onPartialUpdate?.(data.text || '');
      // 有新的转录结果，清除之前的错误提示
      if (data.text) {
        setError(null);
      }
    });

    eventSource.addEventListener('final', (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      if (data.text) {
        setFinalText((prev) => {
          const newText = prev + data.text + ' ';
          onTranscriptionUpdate?.(data.text, newText);
          return newText;
        });
        setPartialText(''); // 清空部分文本
        setError(null);
      }
    });

    eventSource.addEventListener('error', (event: MessageEvent) => {
      // 区分后端发送的自定义 error 事件和浏览器原生 SSE 连接错误
      // 原生连接错误的 event.data 为 undefined，由 onerror 处理重连，无需外显
      if (!event.data) {
        console.warn('SSE 原生连接错误（将自动重连），忽略外显');
        return;
      }

      console.error('转录错误:', event.data);
      const eventStr = typeof event.data === 'string' ? event.data : JSON.stringify(event.data);
      setErrorWithLog('转录错误: ' + eventStr, 'transcription-error');
    });

    eventSource.onerror = (event: Event) => {
      const readyState = eventSource.readyState;
      console.error('SSE 连接错误:', {
        event,
        readyState,
        url: eventSource.url,
        sessionId: sessionIdRef.current,
        timestamp: new Date().toISOString(),
      });

      // readyState=0 表示连接未成功建立（可能是超时或后端响应慢），应该重试
      // readyState=2 表示连接已关闭，不应该重试
      if (readyState === 0 && retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        console.log(`SSE 连接失败，${retryDelay}ms 后进行第 ${retryCountRef.current} 次重试...`);

        // 清除之前的重试定时器
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
        }

        // 延迟重试
        retryTimerRef.current = setTimeout(() => {
          if (isRecording) { // 只有在录音状态下才重试
            connectSSE().catch(err => {
              console.error('重试连接失败:', err);
            });
          }
        }, retryDelay);

        if (retryCountRef.current % 5 === 0) {
          let msg = `SSE 连接失败，已重试 ${retryCountRef.current} 次`;
          console.log(msg);
          handleError(new Error(msg), 'SSE 连接失败', moduleName);
        }

      } else {
        // 达到最大重试次数或 readyState=2，显示最终错误
        const errorMsg = readyState === 0
          ? `连接失败，已重试 ${retryCountRef.current} 次，请检查网络或稍后重试`
          : `实时连接中断 (readyState: ${readyState})，请重试`;
        setErrorWithLog(errorMsg, 'sse-connection-error', event);
      }
    };

    setIsConnected(true);
  }, [generateSessionId, onPartialUpdate, onTranscriptionUpdate, setErrorWithLog, isRecording, retryDelay, maxRetries]);

  /**
   * 申请麦克风权限
   */
  const requestMicrophonePermission = useCallback(async () => {
    try {
      setPermissionError(null);

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // 权限申请成功，立即停止流以释放资源
      stream.getTracks().forEach((track) => track.stop());

      setMicrophonePermission('granted');
      message.success('麦克风权限已获取');
    } catch (err: any) {
      console.error('麦克风权限申请失败:', err);
      setMicrophonePermission('denied');
      setPermissionError(
        '麦克风权限申请失败: ' +
          err.message +
          '\n请在浏览器设置中允许麦克风访问权限'
      );
    }
  }, []);

  /**
   * 开始录音和转录
   */
  const startRecording = useCallback(async () => {
    try {
      setError(null);

      // 检查是否达到时长上限
      if (RecordingDurationStore.checkLimit()) {
        message.error('录音时长已达上限，无法继续录音');
        setErrorWithLog('录音时长已达上限，请联系管理员', 'duration-limit-reached');
        return;
      }

      // 建立 SSE 连接
      await connectSSE();

      // 开始 PCM 录音
      await startRec();
      setIsRecording(true);

      // 开始全局用量计时
      RecordingDurationStore.start();

      // 开始本次会议时长计时
      startMeetingTimer();

      // 开始本段录音计时（归零并开始）
      startSegmentTimer();

      // 回调
      onStart?.();
    } catch (err: any) {
      console.error('开始录音失败:', err);
      setErrorWithLog('开始录音失败: ' + err.message, 'start-recording-error');
      setIsRecording(false);
    }
  }, [connectSSE, onStart, setErrorWithLog, startRec]);

  /**
   * 停止录音和转录
   */
  const stopRecording = useCallback(async () => {
    try {
      // 清除重试定时器
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }

      // 停止全局用量计时
      RecordingDurationStore.stop();

      // 停止本次会议时长计时
      stopMeetingTimer();

      // 停止本段录音计时并上报增量
      stopSegmentTimer();
      if (user?.id && SegmentDurationStore.currentDuration > 0) {
        reportRecordingDuration(user.id, SegmentDurationStore.currentDuration).then(() => {
          resetSegmentTimer();
        });
      }

      // 停止 PCM 录音
      await stopRec();

      // 发送停止信号
      if (sessionIdRef.current) {
        await fetch(
          `${API_BASE_URL}/api/speech/stop?sessionId=${sessionIdRef.current}`,
          { method: 'POST' }
        );
      }

      // 关闭 SSE 连接
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }

      setIsRecording(false);
      setIsConnected(false);

      // 回调，传入最终文本
      onStop?.(finalTextRef.current);
    } catch (err: any) {
      console.error('停止录音失败:', err);
      setErrorWithLog('停止录音失败: ' + err.message, 'stop-recording-error');
      setIsRecording(false);
    }
  }, [onStop, setErrorWithLog, stopRec, user?.id]);

  /**
   * 清空转录内容
   */
  const clearTranscription = useCallback(() => {
    setFinalText('');
    setPartialText('');
    setError(null);
  }, []);

  /**
   * 获取录音文件（WAV 格式）
   */
  const getRecordingFile = useCallback((fileName?: string): File | null => {
    const pcmDataList = getAllPcmData();
    if (pcmDataList.length === 0) {
      console.warn('没有可用的录音数据');
      return null;
    }
    
    const defaultFileName = fileName || `inmeet_recording_${Date.now()}.wav`;
    const audioFile = createAudioFile(pcmDataList, defaultFileName, 16000, 1);
    console.log(`生成录音文件: ${audioFile.name}, 大小: ${(audioFile.size / 1024 / 1024).toFixed(2)}MB`);
    
    return audioFile;
  }, [getAllPcmData]);

  // 监听时长上限，到达后自动停止录音
  useEffect(() => {
    if (durationState.isLimitReached && isRecording) {
      message.warning('录音时长已达上限，自动停止录音');
      stopRecording();
    }
  }, [durationState.isLimitReached, isRecording, stopRecording]);

  // 组件卸载时清理资源
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      // 清除重试定时器
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      // 如果卸载时仍在录音，先停止计时并上报
      stopMeetingTimer();
      stopSegmentTimer();
      if (SegmentDurationStore.currentDuration > 0) {
        const uid = user?.id;
        if (uid) {
          reportRecordingDuration(uid, SegmentDurationStore.currentDuration);
        }
      }
      resetSegmentTimer();
      RecordingDurationStore.stop();
      // 重置全局用量计时器
      RecordingDurationStore.reset();
      // 重置本次会议时长计时器
      resetMeetingTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    isRecording,
    isConnected,
    finalText,
    partialText,
    error,
    microphonePermission,
    permissionError,
    durationState,
    startRecording,
    stopRecording,
    clearTranscription,
    requestMicrophonePermission,
    setFinalText,
    getRecordingFile,
  };
}

export default useRealtimeTranscription;
